/**
 * ============================================================================
 * LINK MANAGER COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Comprehensive link management component
 */

import React, { useState } from 'react';
import { useLinks } from '../../hooks/useLinks';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import Button from '../common/Button';
import Tabs from '../common/Tabs';
import LinkGenerator from './LinkGenerator';
import LinkPreview from './LinkPreview';
import LinkAnalytics from './LinkAnalytics';
import QRCode from './QRCode';
import ShareLinks from '../transaction/ShareLinks';

/**
 * Link Manager Component
 * @param {Object} props
 * @param {Object} props.transaction - Transaction object
 * @param {string} props.transactionId - Transaction ID (optional if transaction provided)
 * @returns {JSX.Element}
 */
const LinkManager = ({ transaction, transactionId }) => {
  const { language } = useI18n();
  const [activeTab, setActiveTab] = useState('generate');
  const [generatedLink, setGeneratedLink] = useState(transaction?.shortLink || '');

  const txId = transactionId || transaction?.id;

  const tabs = [
    {
      id: 'generate',
      label: language === 'fr' ? 'Générer' : 'Generate',
      icon: '🔗',
    },
    {
      id: 'preview',
      label: language === 'fr' ? 'Aperçu' : 'Preview',
      icon: '👁️',
    },
    {
      id: 'share',
      label: language === 'fr' ? 'Partager' : 'Share',
      icon: '📤',
    },
    {
      id: 'qr',
      label: 'QR Code',
      icon: '📱',
    },
    {
      id: 'analytics',
      label: language === 'fr' ? 'Statistiques' : 'Analytics',
      icon: '📊',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header - Only show if not in page context */}
      {!transaction && (
        <div className="animate-fadeInUp">
          <h2 className="text-3xl font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent mb-2">
            {language === 'fr' ? 'Gestion des liens' : 'Link Management'}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {language === 'fr'
              ? 'Générez, partagez et suivez vos liens de transaction'
              : 'Generate, share, and track your transaction links'}
          </p>
        </div>
      )}

      {/* Tabs */}
      <Card variant="glass" className="animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
        <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} variant="pills" />
      </Card>

      {/* Tab Content */}
      <div className="animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
        {activeTab === 'generate' && (
          <LinkGenerator
            transactionId={txId}
            onLinkGenerated={(link) => setGeneratedLink(link)}
          />
        )}

        {activeTab === 'preview' && (
          <LinkPreview transaction={transaction} shortLink={generatedLink || transaction?.shortLink} />
        )}

        {activeTab === 'share' && transaction && (
          <ShareLinks transaction={transaction} />
        )}

        {activeTab === 'qr' && (
          <QRCode
            transactionId={txId}
            shortCode={transaction?.shortCode}
            url={generatedLink || transaction?.shortLink}
          />
        )}

        {activeTab === 'analytics' && (
          <LinkAnalytics transactionId={txId} shortCode={transaction?.shortCode} />
        )}
      </div>
    </div>
  );
};

export default LinkManager;
